import {AxiosError} from "axios";
import {LOCAL_STORAGE_PICK_KEY} from "data/constants";
import {Bindings} from "data/constants/bindings";
import {ModalType, PlayedGames, RoundStatus} from "data/enums";
import {IPickResponse} from "data/providers/api/pinpoint.api.provider";
import {IApiResponse} from "data/services/http";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IPinpointStore} from "data/stores/pinpoint/pinpoint.store";
import type {IRoundsStore, ISessionWithPick} from "data/stores/rounds/rounds.store";
import type {IShareStore} from "data/stores/share/share.store";
import type {IUserStore} from "data/stores/user/user.store";
import {ViewController} from "data/types/structure";
import {extractErrorMessage} from "data/utils";
import dayjs from "dayjs";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable, reaction} from "mobx";

interface IControllerProps {
	params: {id: string};
}

export interface IPickPageController extends ViewController<IControllerProps> {
	get i18n(): ILocalizationStore;

	get round(): ISessionWithPick | null;
	get lockTime(): string;

	get isLongestDriveFilled(): boolean;
	get isNearestPinFilled(): boolean;
	get isPickFilled(): boolean;
	get isSubmitDisabled(): boolean;

	submitPick: () => void;
}

@injectable()
export class PickPageController implements IPickPageController {
	@observable _roundId?: number | null;
	@observable private _playedDisposer?: ReturnType<typeof reaction>;
	@observable private _showPredictModalDisposer?: ReturnType<typeof reaction>;

	constructor(
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.PinpointStore) private _pinpointStore: IPinpointStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.ShareStore) private _shareStore: IShareStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	get round() {
		if (!this._roundId) {
			return null;
		}

		return this._roundStore.getRoundById(this._roundId);
	}

	get lockTime() {
		return dayjs(this.round?.startDate).format("MMM D, H:mm");
	}

	get pick() {
		if (!this._roundId) {
			return null;
		}

		return this._pinpointStore.getPickByRoundId(this._roundId);
	}

	get isLongestDriveFilled() {
		return this._pinpointStore.isLongestDriveFilled(this._roundId!);
	}

	get isNearestPinFilled() {
		return this._pinpointStore.isNearestPinFilled(this._roundId!);
	}

	get isPickFilled() {
		return this.isLongestDriveFilled && this.isNearestPinFilled;
	}

	get isDataFormChanged() {
		return this._pinpointStore.isPickChanged;
	}

	get showPredictModal() {
		return this.isLongestDriveFilled && !this.isNearestPinFilled;
	}

	get isSubmitDisabled() {
		return (
			!this.isDataFormChanged ||
			!this.isPickFilled ||
			this.round?.pinpointStatus !== RoundStatus.Scheduled
		);
	}

	@action submitPick = async () => {
		if (!this.pick) {
			return;
		}

		if (this._userStore.isAuthorized) {
			await this._pinpointStore.submitPick(this.pick);

			void this._shareStore.shareTeam({roundId: this._roundId!});
			this._shareStore.openShareModal({roundId: this._roundId!});
		} else {
			localStorage.setItem(LOCAL_STORAGE_PICK_KEY, JSON.stringify(this.pick));
			this._modalsStore.showModal(ModalType.LOSE_PICKS);
		}
	};

	@action private onError = (error: AxiosError<IApiResponse>) => {
		this._modalsStore.showModal(ModalType.ERROR, {
			message: extractErrorMessage(error),
		});
	};

	@action private predictModalInit = () => {
		this._showPredictModalDisposer = reaction(
			() => this.showPredictModal,
			(showPredictModal) => {
				if (!showPredictModal) {
					return;
				}

				this._modalsStore.showModal(ModalType.PREDICT);
			}
		);
	};

	dispose() {
		this._playedDisposer?.();
		this._showPredictModalDisposer?.();
	}

	async init({params}: IControllerProps) {
		this._roundId = Number(params.id) || null;

		if (this._userStore.isAuthorized) {
			if ([this.round?.id, !this.pick].every(Boolean)) {
				await this._pinpointStore.fetchMyPick(this.round!.id!).catch(this.onError);
			}

			if (
				[this.round?.id, this.round?.pinpointStatus === RoundStatus.Scheduled].every(
					Boolean
				)
			) {
				const pick = JSON.parse(
					localStorage.getItem(LOCAL_STORAGE_PICK_KEY) || "null"
				) as IPickResponse | null;

				if (pick) {
					this._pinpointStore.updatePickByRoundId(this.round!.id!, pick);

					this._playedDisposer = reaction(
						() => this._userStore.user?.playedGames,
						(playedGames) => {
							if (!playedGames?.includes(PlayedGames.Pinpoint)) {
								return;
							}

							void (async () => {
								await this._pinpointStore.submitPick(pick).finally(() => {
									localStorage.removeItem(LOCAL_STORAGE_PICK_KEY);
								});

								void this._shareStore.shareTeam({roundId: this.round!.id!});
								this._shareStore.openShareModal({roundId: this.round!.id!});
							})();
						},
						{fireImmediately: true}
					);
				}
			}
		}

		this.predictModalInit();
	}
}
